import React, { useMemo, forwardRef, useRef, useImperativeHandle, useCallback } from 'react';
import {
  TablePaginatedV8,
  order2Sorting,
  TableCellV8,
  EditCellV8,
  TableDataState,
  promiseModalDialog,
  toastSuccess,
  CellDateV8,
  EyeColumnV8,
} from 'motion-components';
import { generatePath, useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';

import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import { gridInactiveRowStyle, gridRowSelectedStyle } from 'shared-pages/common/grid-helpfunc';
import * as Lo from 'lodash';
import { FormInput, gqlAdd, gqlDelete, gqlUpdate } from '../graphql';
import { useMutation } from '@apollo/client';
import { getRouteByName } from 'app/routes';
import { RouteStatusBadge } from '../route-status-badge';

/* ----------- Grid ------------------ */

export const DataGrid = forwardRef((props: any, ref) => {
  const {
    data,
    dataCount,
    initPageIndex,
    onPageChange,
    loading,
    handleExport,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    refetch,
  } = props;
  const location = useLocation<any>();
  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t, i18n } = useTranslation();
  const [updateContainer] = useMutation(gqlUpdate);
  const [addNewContainer] = useMutation(gqlAdd);
  const [deleteContainer] = useMutation(gqlDelete);

  const rowClicked = useCallback(
    (row) => {
      history.push({ pathname: generatePath(getRouteByName('Route'), { id: row.id }) });
    },
    [history, location.pathname, location.state],
  );
  const columns = useMemo(
    () => [
      EyeColumnV8(rowClicked),
      // {
      //   id: 'id',
      //   header: t('ID'),
      //   columnName: t('ID'),
      //   accessorKey: 'id',
      //   orderByField: 'id',
      //   cell: CellCenterV8,
      //   size: 5,
      // },
      {
        id: 'code',
        header: t('Κωδικός Δρομολογίου'),
        columnName: t('Κωδικός Δρομολογίου'),
        accessorKey: 'code',
        orderByField: 'code',
        cell: TableCellV8,
        size: 10,
        meta: {
          type: 'text',
          disabled: true,
        },
      },
      {
        id: 'routeDate',
        header: t('Ημερομηνία Δρομολογίου'),
        columnName: t('Ημερομηνία Δρομολογίου'),
        accessorKey: 'routeDate',
        orderByField: 'routeDate',
        cell: CellDateV8,
        size: 18,
        meta: { type: 'text' },
      },
      {
        id: 'collectorId',
        header: t('Συλλέκτης'),
        columnName: t('Συλλέκτης'),
        accessorKey: 'vehicle.collector.name',
        orderByField: 'vehicle.collector.name',
        cell: TableCellV8,
        size: 25,
        meta: { type: 'text' },
      },
      {
        id: 'vehicleId',
        header: t('Όχημα'),
        columnName: t('Όχημα'),
        accessorKey: 'vehicle.plateNumber',
        orderByField: 'vehicle.plateNumber',
        cell: TableCellV8,
        size: 15,
        meta: { type: 'text' },
      },
      {
        id: 'driverId',
        header: t('Οδηγός'),
        columnName: t('Οδηγός'),
        accessorKey: 'driver.name',
        orderByField: 'driver.name',
        cell: TableCellV8,
        size: 15,
        meta: { type: 'text' },
      },
      {
        id: 'notes',
        header: t('Σημειώσεις'),
        columnName: t('Σημειώσεις'),
        accessorKey: 'notes',
        orderByField: 'notes',
        cell: TableCellV8,
        size: 40,
        meta: { type: 'text' },
      },
      {
        id: 'dateCompleted',
        header: t('Ημερομηνία Ολοκλήρωσης'),
        columnName: t('Ημερομηνία Ολοκλήρωσης'),
        accessorKey: 'dateCompleted',
        orderByField: 'dateCompleted',
        cell: CellDateV8,
        size: 18,
        meta: { type: 'text' },
      },
      {
        id: 'state.name',
        header: t('Κατάσταση Δρομολογίου'),
        columnName: t('Κατάσταση Δρομολογίου'),
        accessorKey: 'state.name',
        orderByField: 'state.name',
        cell: ({ row }) => {
          const data = row.original;

          return (
            <div className="d-flex align-items-center justify-content-center w-100 h-100">
              <RouteStatusBadge name={data.state.name} id={data.statusId} />
            </div>
          );
        },
        size: 10,
        meta: { type: 'text' },
      },
    ],
    [t],
  );

  const tableRef = useRef(null);
  // return of useRef
  useImperativeHandle(ref, () => (tableRef.current ? tableRef.current : null));

  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      {data ? (
        <TablePaginatedV8
          ref={tableRef}
          loading={loading}
          id="containers-grid"
          appStore={appStore}
          tableStore={tableStore}
          key="grid"
          columns={columns as any}
          data={data}
          dataCount={dataCount}
          topBorderRadius={false}
          onRowClick={(table) => setSelected(table.row.original)}
          rowProps={(row: any) => {
            let style = undefined;
            if (row.original.id === selected?.id) style = gridRowSelectedStyle;
            if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
            return { style: style };
          }}
          onExport={handleExport}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(sort) => {
            console.log('$$$$ grid sortbychange', sort);
            // $$$
            const columnId = sort[0].id;
            const sortOrder = sort[0].desc ? 'DESC' : 'ASC';
            // find column by id
            const cols = columns.filter((r: any) => r.id === columnId);
            if (cols.length === 1) {
              const col = cols[0] as any;
              let order = col.orderByField ? col.orderByField : 'id';
              order = order + ' ' + sortOrder;
              onOrderChange(order);
            }
          }}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
});
